// Presentation pieces shared by AdminDashboard and EmployeeDashboard.
//
// Both screens are built from the same vocabulary — an uppercase eyebrow, a
// metric tile, a sectioned card head, a pill-shaped link, a bordered table —
// so it lives here once rather than being copy-pasted into each file.
// Everything reads from the design tokens in index.css, so it follows the theme.

import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Card } from "../../Components/ui";

export const eyebrowStyle = {
  fontSize: 11, fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.1em", color: "var(--text-tertiary)",
};

// Compact padding for EmptyState when it sits inside a card rather than a page.
export const emptyPad = { padding: "26px 16px" };

// Metric-tile accents. Each entry pairs a background with a foreground token,
// and both flip with the theme, so the chip stays readable either way.
export const TILE_TONES = {
  brand:   { bg: "var(--accent-soft)",     fg: "var(--accent-text)" },
  success: { bg: "var(--tone-success-bg)", fg: "var(--tone-success-fg)" },
  warning: { bg: "var(--tone-warning-bg)", fg: "var(--tone-warning-fg)" },
  danger:  { bg: "var(--tone-danger-bg)",  fg: "var(--tone-danger-fg)" },
  info:    { bg: "var(--tone-info-bg)",    fg: "var(--tone-info-fg)" },
  violet:  { bg: "var(--tone-violet-bg)",  fg: "var(--tone-violet-fg)" },
};

export function MetricTile({ icon, tone = "brand", label, value }) {
  const t = TILE_TONES[tone] || TILE_TONES.brand;
  return (
    <Card padding="none" style={{ overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px" }}>
        {icon && (
          <span aria-hidden="true" style={{
            width: 44, height: 44, borderRadius: 12, flex: "0 0 auto",
            background: t.bg, color: t.fg, fontSize: 21,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>{icon}</span>
        )}
        <div style={{ minWidth: 0 }}>
          {/* The number stays --text-primary. In the old rainbow treatment it was
              tinted (#10B981 on white is 2.2:1), which failed AA outright. The
              tone now lives in the chip, where it only has to clear the 3:1 bar
              for a non-text UI element. */}
          <div className="tabular" style={{ fontSize: 28, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1, letterSpacing: "-0.02em" }}>{value}</div>
          <div style={{ ...eyebrowStyle, marginTop: 5 }}>{label}</div>
        </div>
      </div>
      {/* Hairline in the tone colour, so the six employee tiles stay tellable
          apart at a glance without colouring the number itself. */}
      <div style={{ height: 3, background: t.fg, opacity: 0.85 }} />
    </Card>
  );
}

export function IconChip({ children, size = 30 }) {
  return (
    <span aria-hidden="true" style={{
      width: size, height: size, borderRadius: 9, flex: "0 0 auto",
      background: "var(--accent-soft)", color: "var(--accent-text)", fontSize: size / 2,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}>{children}</span>
  );
}

export function SectionHead({ icon, title, subtitle, action }) {
  return (
    <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {icon && <IconChip>{icon}</IconChip>}
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 1 }}>{subtitle}</div>}
        </div>
      </div>
      {action}
    </div>
  );
}

// Secondary, pill-shaped nav link for the greeting header.
export function PillLink({ to, icon, label }) {
  return (
    <RouterLink to={to} style={{
      background: "var(--bg-surface)", color: "var(--text-secondary)",
      border: "1px solid var(--border-default)",
      padding: "8px 15px", borderRadius: 9999, fontSize: 13, fontWeight: 500,
      textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 7,
    }}>{icon} {label}</RouterLink>
  );
}

// Primary action in the greeting header. Brand-red fill with white text is
// 4.69:1, which clears AA for normal text as well as UI components.
export const primaryPillStyle = {
  background: "var(--accent-primary)", color: "#FFFFFF",
  padding: "9px 18px", borderRadius: 9999, fontSize: 13, fontWeight: 600,
  textDecoration: "none", border: "1px solid transparent", cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 7,
  boxShadow: "var(--shadow-glow)",
  fontFamily: "inherit",
};

/* ── Table bits ────────────────────────────────────────────────── */

export const tableStyle = { width: "100%", fontSize: 13, borderCollapse: "collapse" };
const thStyle = {
  textAlign: "left", padding: "10px 18px", fontSize: 11, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.08em",
  // --text-secondary, not tertiary: measured in the browser, tertiary on the
  // subtle header band is only 4.20:1 in dark mode. Secondary is 7.3:1 there
  // and 7.7:1 in light, and at 11px/700 uppercase it still reads as a header.
  color: "var(--text-secondary)", background: "var(--bg-subtle)",
  borderBottom: "1px solid var(--border-subtle)",
};
const tdBase = { padding: "11px 18px", borderTop: "1px solid var(--border-subtle)", color: "var(--text-primary)" };

export function Th({ children }) { return <th style={thStyle}>{children}</th>; }
export function Td({ children, style, className }) {
  return <td style={{ ...tdBase, ...style }} className={className}>{children}</td>;
}
